import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AddUserToTeamGroupType,
  Application,
  CombineApplicationsView,
  ContactUsData,
  CreateApplicationData,
  ExternalApiDetails,
  PromoteRemoveUserInTeamGroup,
  SignupData,
  UpdateAppWebsiteData,
  UpdateAppOwnerData,
  UpdateDelegateIdsData,
} from '../store';
import { UserContextService } from './user-context.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  // shouldGetAllApis = new BehaviorSubject<number | null>(null);
  private dataApplication = new BehaviorSubject<any>(null);
  data$ = this.dataApplication.asObservable();
  private serviceId: string | null = null;
  constructor(
    private http: HttpClient,
    private userContextService: UserContextService
  ) {}

  setData(data: any): void {
    this.dataApplication.next(data);
  }

  setServiceId(id: string): void {
    this.serviceId = id;
  }

  getServiceId(): string | null {
    return this.serviceId;
  }

  getExternalApiData(): Observable<any> {
    return this.http
      .get('/api/ext/services', {
        headers: {
          'X-User': this.userContextService.getTokenInfo()?.token,
        },
      })
      .pipe(map((result: any[]) => result));
  }

  subscribeToOneApiForManyApplications(apiId: string, applications: string[]): Observable<any> {
    const body = {
      applicationIds: applications,
      receiveEmailNotifications: true,
    };
    return this.http.put(`/api/ext/services/${apiId}/servicesubscription`, body);
  }

  subscribeToManyApisForOneApplication(applicationId: string, apis: string[]): Observable<any> {
    const body = {
      serviceIds: apis,
      receiveEmailNotifications: true,
    };
    // apis.forEach((apiId: any) => {
    //   console.log(apiId);
    // });
    return this.http.put(`/api/ext/applications/${applicationId}/submitservicesubscription`, body);
  }

  getApiDetail(apiId: string): Observable<any> {
    return this.http.get(`/api/ext/services/${apiId}`).pipe(map((result: ExternalApiDetails) => result));
  }

  createApplication(appData: CreateApplicationData): Observable<any> {
    return this.http.post(`/api/ext/applications`, appData).pipe(map((result: any) => result));
  }

  updateApplicationWebsite(websiteData: UpdateAppWebsiteData): Observable<any> {
    const appData = {
      website: websiteData.website,
    };
    return this.http.put(`/api/ext/applications/${websiteData.appId}`, appData).pipe(map((result: any) => result));
  }

  updateApplicationOwner(ownerData: UpdateAppOwnerData): Observable<any> {
    const appData = {
      newOwner: ownerData.emailId,
    };
    return this.http
      .put(`/api/ext/applications/${ownerData.appId}/update-management`, appData)
      .pipe(map((result: any) => result));
  }

  updateApplicationDelegateIds(websiteData: UpdateDelegateIdsData): Observable<any> {
    const appData = {
      delegates: websiteData.delegates,
    };
    return this.http
      .put(`/api/ext/applications/${websiteData.appId}/update-management`, appData)
      .pipe(map((result: any) => result));
  }

  addProdUri(redirectUriData: any): Observable<any> {
    const appData = {
      prodRedirectUrls: redirectUriData.redirectUrls,
    };
    return this.http.put(`/api/ext/applications/${redirectUriData.appId}`, appData).pipe(map((result: any) => result));
  }

  addSandboxUri(redirectUriData: any): Observable<any> {
    const appData = {
      sandboxRedirectUrls: redirectUriData.redirectUrls,
    };
    return this.http.put(`/api/ext/applications/${redirectUriData.appId}`, appData).pipe(map((result: any) => result));
  }

  private normalizePublicJwksString(publicJwks: string): string {
    try {
      const parsed = JSON.parse(publicJwks);
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.keys)) {
        return JSON.stringify(parsed);
      }
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return JSON.stringify({ keys: [parsed] });
      }
      return publicJwks;
    } catch {
      return publicJwks;
    }
  }

  appProdJwksUrl(redirectUriData: any): Observable<any> {
    const appData: any = {
      clientFor: redirectUriData.persona,
    };
    const prodJwksUrl = redirectUriData.jwksUrl ?? redirectUriData.redirectUrls?.[0];
    if (prodJwksUrl) {
      appData.prodJwksUrl = prodJwksUrl;
    }
    if (
      redirectUriData.prodPublicJwks !== undefined &&
      redirectUriData.prodPublicJwks !== null &&
      redirectUriData.prodPublicJwks !== ''
    ) {
      appData.prodPublicJwks = this.normalizePublicJwksString(redirectUriData.prodPublicJwks);
    }
    console.log('[appProdJwksUrl] Final payload:', appData);
    return this.http.put(`/api/ext/applications/${redirectUriData?.appId}`, appData).pipe(map((result: any) => result));
  }

  appSandboxJwksUrl(redirectUriData: any): Observable<any> {
    const appData: any = {
      clientFor: redirectUriData.persona,
    };
    const sandboxJwksUrl = redirectUriData.jwksUrl ?? redirectUriData.redirectUrls?.[0];
    if (sandboxJwksUrl) {
      appData.sandboxJwksUrl = sandboxJwksUrl;
    }
    if (
      redirectUriData.sandboxPublicJwks !== undefined &&
      redirectUriData.sandboxPublicJwks !== null &&
      redirectUriData.sandboxPublicJwks !== ''
    ) {
      appData.sandboxPublicJwks = this.normalizePublicJwksString(redirectUriData.sandboxPublicJwks);
    }
    console.log('[appSandboxJwksUrl] Final payload:', appData);
    return this.http.put(`/api/ext/applications/${redirectUriData?.appId}`, appData).pipe(map((result: any) => result));
  }

  createUser(signUpData: SignupData): Observable<any> {
    return this.http.post(`/api/ext/users`, signUpData);
  }

  sendResetPasswordEmal(emailToSend: string): Observable<any> {
    const body = {
      email: emailToSend,
    };
    return this.http.put(`/api/ext/users/flagPassword`, body);
  }

  getExternalApplicationsData(): Observable<CombineApplicationsView> {
    return this.http.get(`/api/ext/applications`).pipe(map((result: CombineApplicationsView) => result));
  }

  sendContactUsEmail(contactUsData: ContactUsData): Observable<any> {
    return this.http.post(`/api/ext/notifications/contactus`, contactUsData).pipe(map((result: any) => result));
  }

  getDownloadDocumentById(serviceId: string, documentId: string): Observable<any> {
    return this.http
      .get(`/api/ext/services/${serviceId}/documents/${documentId}`, { responseType: 'blob' })
      .pipe(map(blob => URL.createObjectURL(blob)));
  }

  unsubscribeAPI(applicationId: string, serviceId: any): Observable<any> {
    return this.http.put(`/api/ext/applications/${applicationId}/unsubscribe/${serviceId}`, undefined);
  }

  getExternalApplicationByID(appId: string): Observable<Application> {
    return this.http.get(`/api/ext/applications/${appId}`).pipe(map((result: Application) => result));
  }

  deleteApplication(appId: string): Observable<any> {
    return this.http.delete(`/api/ext/applications/${appId}`);
  }
  generateClientId(appId: string, environment: any): Observable<any> {
    return this.http.post(`/api/ext/applications/${appId}/client/generate`, environment);
  }
  regenerateClientId(appId: string, environment: any): Observable<any> {
    return this.http.post(`/api/ext/applications/${appId}/client/regenerate`, environment);
  }
  generatePayerClientId(appId: string, payload: any): Observable<any> {
    return this.http.post(`/api/ext/applications/${appId}/current-payer-provider-client/generate`, payload);
  }

  getContactUsCategories(supportRequestApi: string): Observable<any> {
    supportRequestApi = supportRequestApi ? supportRequestApi : 'none';
    return this.http.get(
      `/api/ext/notifications/external/support?supportRequestApi=${encodeURIComponent(supportRequestApi)}`
    );
  }

  changeIpWhitelisting(serviceId: string, applicationId: string, payload): Observable<any> {
    return this.http.post(
      `/api/ext/allowlist-resources?serviceId=${serviceId}&applicationId=${applicationId}`,
      payload
    );
  }

  setTeamGroup(setTeamGroupAction: any): Observable<any> {
    return this.http.post(`/api/ext/profile-management`, setTeamGroupAction);
  }

  addUserToTeamGroup(addUserToTeamGroup: AddUserToTeamGroupType): Observable<any> {
    return this.http.put(`/api/ext/profile-management`, addUserToTeamGroup);
  }

  removeUserFromTeamGroup(removeUserFromTeamGroup: PromoteRemoveUserInTeamGroup): Observable<any> {
    return this.http.put(`/api/ext/profile-management/leaveGroup`, removeUserFromTeamGroup);
  }
  promoteUserToOwner(promoteUserInTeamGroup: PromoteRemoveUserInTeamGroup): Observable<any> {
    return this.http.put(`/api/ext/profile-management/transferGroupOwnership`, promoteUserInTeamGroup);
  }

  getQuestionnaireData(externalServiceId: string, applicationId: string): Observable<any> {
    return this.http
      .get(`/api/ext/questionnaire/${externalServiceId}/${applicationId}`)
      .pipe(map((result: any) => result));
  }

  /**
   * Submit questionnaire response for external users
   * NOTE: External users can only POST new responses - responses are immutable after submission
   * @param applicationId - The external application ID
   * @param payload - The questionnaire response payload
   * @returns Observable<any> - The response from the server
   */
  submitQuestionnaire(applicationId, payload): Observable<any> {
    // External users always POST to submit responses
    // Responses are immutable - no editing/PUT allowed
    return this.http.post(`/api/ext/questionnaire/${applicationId}`, payload).pipe(map((result: any) => result));
  }

  submitQuestionnaireForApproval(applicationId: string, payload: any): Observable<any> {
    return this.http
      .post(`/api/ext/questionnaire/${applicationId}/submit-for-approval`, payload)
      .pipe(map((result: any) => result));
  }

  refreshUser(): Observable<any> {
    return this.http.post(`/api/ext/users/refresh-user`, {}).pipe(map((result: any) => result));
  }
}
